import { Injectable } from '@angular/core';
import { AndroidPermissions } from '@awesome-cordova-plugins/android-permissions/ngx';
import { BluetoothSerial } from '@awesome-cordova-plugins/bluetooth-serial/ngx';
import AsyncUtils from '../utility/async-utils';
import { DesktopService } from './desktop.service';
import { DesktopBluetoothService } from './desktop-bluetooth.service';

export interface IBluetoothDevice {
  id: string;
  uuid: string;
  address: string;
  class: number;
  name: string;
  rssi: number;
}

/** Minimum time to let the printer drain the TX buffer after a write. */
const MIN_WRITE_FLUSH_MS = 3000;
const WRITE_DRAIN_BYTES_PER_SECOND = 4000;

@Injectable({ providedIn: 'root' })
export class BluetoothService {
  private _connectedAddress: string | null = null;
  private _operationQueue: Promise<void> = Promise.resolve();

  constructor(
    private _bluetoothSerial: BluetoothSerial,
    private _androidPermissions: AndroidPermissions,
    private _desktop: DesktopService,
    private _desktopBluetooth: DesktopBluetoothService
  ) {}

  async getConnectedDevices(): Promise<IBluetoothDevice[]> {
    if (this._desktop.isDesktop) {
      return this._desktopBluetooth.getConnectedDevices();
    }

    await this._checkPermissions(
      this._androidPermissions.PERMISSION.BLUETOOTH_SCAN
    );
    await this._checkPermissions(
      this._androidPermissions.PERMISSION.BLUETOOTH_CONNECT
    );
    await this._turnOnBluetooth();
    return this._bluetoothSerial.list();
  }

  sendDataToDevice(
    deviceAddress: string,
    data: string | ArrayBuffer | number[] | Uint8Array
  ): Promise<void> {
    return this._enqueueOperation(() =>
      this._sendDataToDevice(deviceAddress, data)
    );
  }

  private async _sendDataToDevice(
    deviceAddress: string,
    data: string | ArrayBuffer | number[] | Uint8Array
  ): Promise<void> {
    if (this._desktop.isDesktop) {
      await this._desktopBluetooth.sendDataToDevice(deviceAddress, data);
      await this._waitForWriteDrain(data);
      return;
    }

    await this._checkPermissions(
      this._androidPermissions.PERMISSION.BLUETOOTH_CONNECT
    );

    await this._turnOnBluetooth();
    await this._ensureConnected(deviceAddress);
    await this._bluetoothSerial.write(data);
    await this._waitForWriteDrain(data);
  }

  /**
   * Drop the kept connection (call when print queue finishes or switching printers).
   */
  disconnect(): Promise<void> {
    return this._enqueueOperation(() => this._disconnect());
  }

  private async _disconnect(): Promise<void> {
    if (this._desktop.isDesktop) {
      this._connectedAddress = null;
      return this._desktopBluetooth.disconnect();
    }

    this._connectedAddress = null;
    await this._bluetoothSerial
      .isConnected()
      .then(() => this._bluetoothSerial.disconnect())
      .catch(() => {});
  }

  private async _ensureConnected(deviceAddress: string): Promise<void> {
    if (this._connectedAddress && this._connectedAddress !== deviceAddress) {
      await this._disconnect();
    }

    const stillConnected =
      this._connectedAddress === deviceAddress &&
      (await this._isSerialConnected());

    if (stillConnected) {
      return;
    }

    // Clear any stale half-open link before connecting
    await this._bluetoothSerial
      .isConnected()
      .then(() => this._bluetoothSerial.disconnect())
      .catch(() => {});

    const connection = await this._connect(deviceAddress);
    if (!connection) {
      this._connectedAddress = null;
      throw 'Помилка підключення до принтера';
    }

    this._connectedAddress = deviceAddress;
  }

  private async _isSerialConnected(): Promise<boolean> {
    const status = await this._bluetoothSerial
      .isConnected()
      .catch(() => 'NO');
    return status !== 'NO';
  }

  private async _waitForWriteDrain(
    data: string | ArrayBuffer | number[] | Uint8Array
  ): Promise<void> {
    const byteLength =
      data instanceof ArrayBuffer ? data.byteLength : data.length;
    const drainTimeMs = Math.max(
      MIN_WRITE_FLUSH_MS,
      Math.ceil((byteLength / WRITE_DRAIN_BYTES_PER_SECOND) * 1000)
    );

    await AsyncUtils.wait(drainTimeMs);
  }

  private _enqueueOperation(operation: () => Promise<void>): Promise<void> {
    const result = this._operationQueue.then(operation);
    this._operationQueue = result.catch(() => {});
    return result;
  }

  private async _turnOnBluetooth() {
    const isEnabled = await this._bluetoothSerial.isEnabled().catch((res) => {
      return this._bluetoothSerial
        .enable()
        .then((r) => {
          return 'OK';
        })
        .catch((err) => {
          return 'NO';
        });
    });

    if (isEnabled != 'OK') {
      throw 'Необхідно увімкнути Bluetooth.';
    }
  }

  private async _checkPermissions(permission: any) {
    const result = await this._androidPermissions
      .checkPermission(permission)
      .catch(() => ({ hasPermission: false }));

    if (!result.hasPermission) {
      await this._androidPermissions
        .requestPermission(permission)
        .catch(() => ({ hasPermission: false }));
    }
  }

  private async _connect(address: string): Promise<boolean> {
    return new Promise((res, rej) => {
      this._bluetoothSerial.connect(address).subscribe(
        (con) => {
          if (con == 'OK') {
            res(true);
          } else {
            res(false);
          }
        },
        (err) => rej(err)
      );
    });
  }
}
